import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, inject, OnDestroy } from '@angular/core';
import { FormGroup, FormsModule, NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';
import { loadTaxType } from '../../after-login/store/tax/tax.action';
import { AuthService } from '../../services/auth.service';
import { EncryptDecryptService } from '../../services/encrypt-decrypt.service';
import { NotificationsService } from '../../services/notifications.service';
import { TosterService } from '../../services/toster.service';
import { Store } from '@ngrx/store';
import { SelectModule } from 'primeng/select';
import { TranslationService } from '../../services/translation.service';

@Component({
  selector: 'app-login',
  imports: [ReactiveFormsModule,SelectModule,TranslateModule,FormsModule, CommonModule, RouterLink, TranslateModule],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss',
})
export class LoginComponent implements OnDestroy {
    public translationService: TranslationService = inject(TranslationService);
  
  private readonly destroy$ = new Subject<void>();
  private readonly allowedRoles = new Set([
    'hotel-owner',
    'marketplace',
    'employee',
    'customer',
  ]);
langOptions = [
    {
      name: 'English',
      value: 'en',
      code: 'us',
    },
    {
      name: 'العربية',
      value: 'ar',
      code: 'sa',
    },
  ];
  selectedLang: string;
  readonly loginForm: FormGroup;
  showPassword = false;
  submitted = false;

  private store = inject(Store);

  constructor(
    private fb: NonNullableFormBuilder,
    private router: Router,
    private authService: AuthService,
    private tosterService: TosterService,
    private notificationsService: NotificationsService,
    private encryptService: EncryptDecryptService,
  ) {
    this.selectedLang = this.translationService.currentLang;
    this.loginForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required]],
      rememberMe: [false],
    });

    this.loadSavedCredentials();
  }

  get f() {
    return this.loginForm.controls;
  }

  togglePasswordVisibility() {
    this.showPassword = !this.showPassword;
  }
  public onLangChange(lang: string) {
    this.translationService.changeLang(lang);
  }
  openSignUpPopup() {
    window.open(
      'https://www.syncinns.com/partner-with-us',
      'SignUpPopup',
      'width=800,height=600,resizable=yes,scrollbars=yes',
    );
  }

  login() {
    this.submitted = true;
    this.authService.clearSession();
    this.tosterService.clearAll();
    if (this.loginForm.invalid) {
      console.log("this.loginForm.invalid ", this.loginForm.invalid)
      this.loginForm.markAllAsTouched();
      const emailControl = this.f['email'];
      const passwordControl = this.f['password'];

      if (
        emailControl?.errors?.['required'] &&
        passwordControl?.errors?.['required']
      ) {
        this.tosterService.showErrorKey('login.emailPassword');
      } else if (emailControl?.errors?.['required']) {
        this.tosterService.showErrorKey('login.emailRequired');
      } else if (emailControl?.errors?.['email']) {
        this.tosterService.showErrorKey('login.emailInvalid');
      } else if (passwordControl?.errors?.['required']) {
        this.tosterService.showErrorKey('login.passwordRequired');
      }
      return;
    }

    const { email, password, rememberMe } = this.loginForm.getRawValue();
    this.handleRememberMe(email, rememberMe);

    this.authService
      .login({ email, password })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => this.handleLoginSuccess(res),
        error: (errorResponse: HttpErrorResponse) => {
          this.tosterService.showError(errorResponse.error.message);
        },
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private handleRememberMe(email: string, rememberMe: boolean): void {
    if (rememberMe) {
      const encryptData = this.encryptService.encryptData(
        JSON.stringify({ email }),
      );
      localStorage.setItem('savedCredential', encryptData);
      return;
    }

    localStorage.removeItem('savedCredential');
  }

  private handleLoginSuccess(res: any): void {
    const userType = res?.data?.user?.userTypeSlug;

    if (!(res?.status === true && this.allowedRoles.has(userType))) {
      this.tosterService.showSuccessKey('common.toasts.invalidCredentials');
      this.authService.logout();
      return;
    }



    this.tosterService.showSuccess(res.message);
    // this.authService.setAccessToken(res.data.accessToken);
    // this.authService.setRefreshToken(res.data.refreshToken);
    this.authService.setUserId(res.data.user._id);
    this.authService.setUserType(userType);
    localStorage.setItem('verified', res.data.user?.account_status);
    localStorage.setItem('loggedIn', 'true');
    const encryptLoginData = this.encryptService.encryptData(
      JSON.stringify(res?.data),
    );


    const loader = document.getElementById('startup-loader');
    if (loader) {
      loader.style.display = 'none';
    }


    localStorage.setItem('loginData', encryptLoginData);

    const navigateTo =
      userType === 'customer'
        ? ['/syncinns-pms/booking-engine']
        : ['/syncinns-pms'];
    const navigationStart = performance.now();

    this.router.navigate(navigateTo).finally(() => {
      const navigationDuration = performance.now() - navigationStart;
      console.log(
        `[Login] Navigation to ${navigateTo.join('/')} took ${navigationDuration.toFixed(2)}ms`,
      );
      this.schedulePostLoginBackgroundTasks(res.data.user._id);
    });
  }

  private schedulePostLoginBackgroundTasks(userId: string): void {
    const runTasks = () => this.runPostLoginBackgroundTasks(userId);

    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      window.requestIdleCallback(() => runTasks());
      return;
    }

    setTimeout(runTasks, 800);
  }

  private runPostLoginBackgroundTasks(userId: string): void {
    this.notificationsService
      .requestPermissionAndGetToken(userId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (token) =>
          console.log('FCM token handled for logged-in user:', token),
        error: (err) =>
          console.error('Error handling FCM token after login:', err),
      });

    this.store.dispatch(loadTaxType());
  }

  private loadSavedCredentials(): void {
    const credential = localStorage.getItem('savedCredential');

    if (!credential) return;

    try {
      const decryptData = this.encryptService.decryptData(credential);
      const credentials = JSON.parse(decryptData);
      this.loginForm.patchValue({
        email: credentials.email,
        rememberMe: true,
      });
    } catch {
      sessionStorage.clear();
      localStorage.clear();
    }
  }

}
